import { buttonClasses, type Components, menuItemClasses, svgIconClasses, type Theme } from '@mui/material'

import { MenuItemClasses } from '../common/Dropdown/utils'

export const MuiMenu: Components<Theme>['MuiMenu'] = {
    styleOverrides: {
        paper: ({ theme }) => ({
            borderRadius: 8,
            border: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.common.white,
            boxShadow: theme.shadows[4],
        }),
        list: ({ theme }) => ({
            padding: theme.spacing(1, 0),
        }),
    },
}

export const MuiMenuItem: Components<Theme>['MuiMenuItem'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.body2,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderRadius: 4,
            /**
             * `!important`: MUI's own `MenuItemRoot` sets `paddingTop`/`paddingBottom` directly plus
             * `paddingLeft`/`paddingRight` via its own `!disableGutters` variant - both longhand, both
             * beating this shorthand override without it (confirmed via the real computed styles:
             * 6px/16px, MUI's own values, not this rule's 4px/12px).
             */
            padding: `${theme.spacing(1, 3)} !important`,
            margin: theme.spacing(0, 2),
            background: theme.palette.grey[100],
            color: theme.palette.grey[900],
            /** `!important`: MUI's own conditional `minHeight: auto` (at the `sm` breakpoint) otherwise wins over this - Figma's medium row is a fixed 32px, not content-driven */
            minHeight: '32px !important',

            [`&.${menuItemClasses.disabled}`]: {
                opacity: 0.5,
            },

            /**
             * `!important` on `fontSize` here too - without it, this rule (lower specificity, 3 classes)
             * would lose to the unconditional `.${svgIconClasses.root}` rule below (`!important`, 2
             * classes), even though this one is meant to win for icons inside a Button's `endIcon` slot.
             */
            [`& .${buttonClasses.endIcon} .${svgIconClasses.root}`]: {
                ...theme.typography.body1,
                fontSize: `${theme.typography.body1.fontSize} !important`,
            },

            /**
             * The checkmark (`common/Dropdown/MenuItem.tsx`) only renders when selected, so this only
             * ever sizes a visible icon, not reserved empty space. `!important`: empirically, in the
             * real browser, a plain (non-`!important`) version of this rule lost to `CheckIcon`'s own
             * `sx`-generated class despite this selector's higher CSS specificity on paper - the exact
             * cascade mechanism wasn't conclusively identified (CSS layers were checked and ruled out
             * for this theme), so treat this as an empirically-verified, not fully explained, fix.
             */
            [`& .${svgIconClasses.root}`]: {
                fontSize: `${theme.typography.body1.fontSize} !important`,
            },

            [`&.${menuItemClasses.gutters}`]: {
                background: 'none',

                '&:hover': {
                    background: theme.palette.grey[100],
                },

                [`&.${menuItemClasses.selected}`]: {
                    background: theme.palette.grey[100],
                },
            },

            [`&.${MenuItemClasses.small}`]: {
                ...theme.typography.caption,
                margin: theme.spacing(0, 1),
                /** `!important`: needed to beat the base rule's own now-`!important` `32px` above, not just MUI's default */
                minHeight: 'auto !important',

                [`& .${svgIconClasses.root}`]: {
                    fontSize: `${theme.typography.caption.fontSize} !important`,
                },

                [`& .${buttonClasses.endIcon} .${svgIconClasses.root}`]: {
                    ...theme.typography.caption,
                    fontSize: `${theme.typography.caption.fontSize} !important`,
                },
            },
        }),
    },
}
